import {
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Flex,
  Heading,
  Button,
  Input,
  ModalCloseButton,
  Icon,
} from '@chakra-ui/react'
import PromoCodeGraphic from '@/public/assets/images/promo-code-graphic.png'
import { useTranslation } from 'next-i18next/pages'
import Image from 'next/image'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useSecurityApi } from '@/hooks/useSecurityApi'
import { API_GATEWAY } from '@/constants/constants'
import { useMutation } from '@tanstack/react-query'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { TokenMoney, SupportedCurrencies } from '@ninjablaze/sdk-ts'

import { useSession } from '@/context/SessionProvider'
import posthog from 'posthog-js'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { getTokenByDenom } from '@/services/token-service'

type Reward = { quantity: number; denom: string }

export const PromoCodeModal = ({
  onClose,
  initialCode,
  initialReward,
}: {
  onClose: () => void
  initialCode?: string
  initialReward?: Reward
}) => {
  const [reward, setReward] = useState<Reward | null>(initialReward ? initialReward : null)

  return (
    <Modal
      size={initialReward ? 'xs' : 'sm'}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered={true}
      isOpen={true}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent marginLeft={2.5} marginRight={2.5}>
        <ModalCloseButton />
        <ModalBody p={5}>
          {reward ? (
            <RewardContent onClose={onClose} reward={reward} />
          ) : (
            <PromoCode initialCode={initialCode} setReward={setReward} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const RewardContent = ({ reward, onClose }: { reward: Reward; onClose: () => void }) => {
  const { t } = useTranslation()
  const fiatStore = useFiatCurrencyStore()
  const token = new TokenMoney(reward.quantity, reward.denom)
  const isUsdc = getTokenByDenom(reward.denom)?.currency === SupportedCurrencies.USDC
  const isFiatMode = isUsdc && !fiatStore.isUsd()

  const logo = getTokenByDenom(reward.denom).icon(16)

  return (
    <Flex flexDir={'column'} alignItems={'center'} mt={4}>
      <Flex
        mb={2}
        rounded={'32px'}
        bg={'ninja.500'}
        w={'140px'}
        h={'140px'}
        p={9}
        alignItems={'center'}
        justifyContent={'center'}
        boxShadow={'0px 0px 24px 0px rgba(255, 255, 255, 0.24) inset, 0px 0px 40px 0px #C853FF inset;'}
      >
        {logo}
      </Flex>
      <Heading textAlign={'center'} fontSize={'2xl'} mb={1}>
        {t('common.congrats')}
      </Heading>
      <Flex gap={1.5} alignItems={'center'} mb={isFiatMode ? 1 : 4}>
        <Text color={'ninja.100'} fontSize={'xs'} fontWeight={700} textAlign={'center'}>
          {t('promo.you-have-received')}
        </Text>
        <FiatOrCryptoAmount
          iconSize={4}
          wrapperStyles={{
            fontSize: 'sm',
          }}
          value={token}
        />
      </Flex>
      {isFiatMode && (
        <Text color={'ninja.300'} fontSize={'xs'} fontWeight={500} mb={4}>
          ≈ {token.humanFormat}
        </Text>
      )}
      <Button onClick={onClose} w={'full'} rounded={'xl'}>
        {t('promo.lets-play')}
      </Button>
    </Flex>
  )
}

const PromoCode = ({
  initialCode,
  setReward,
}: {
  initialCode?: string
  setReward: Dispatch<SetStateAction<Reward | null>>
}) => {
  const { t } = useTranslation()
  const { error, handleSubmit, isPending, reward, setCode, code } = usePromoCodeForm(initialCode)

  useEffect(() => {
    setReward(reward)
  }, [reward])

  return (
    <Flex flexDir={'column'} alignItems={'center'}>
      <Box width={'174px'} mb={2}>
        <Image src={PromoCodeGraphic} alt="" />
      </Box>
      <Heading textAlign={'center'} fontSize={'2xl'} mb={1}>
        {t('common.promo-code')}
      </Heading>
      <Text color={'ninja.100'} fontSize={'xs'} fontWeight={700} textAlign={'center'} mb={5}>
        {t('common.please-enter-promo-code')}
      </Text>
      <Box w={'full'}>
        <Input
          onChange={(event) => {
            setCode(event.target.value)
          }}
          value={code || ''}
          isDisabled={isPending}
          autoComplete="off"
          h={'52px'}
          border={'1px solid'}
          borderColor={!error ? 'transparent' : 'brand.error'}
          _hover={{
            borderColor: !error ? 'transparent' : 'brand.error',
          }}
          _focus={{ outline: 'none' }}
          _focusVisible={{ outline: 'none' }}
          _active={{ outline: 'none' }}
          bg={'ninja.500'}
          rounded={'2xl'}
          px={4}
          py={4}
          color={'ninja.100'}
          fontWeight={700}
          fontSize={'sm'}
          placeholder={t('common.enter-promo-code')}
          _placeholder={{
            color: 'ninja.300',
            fontWeight: 500,
          }}
        />
      </Box>
      {!!error && (
        <Text ml={4} mt={1} w={'full'} color={'brand.error'} fontWeight={700} fontSize={'xs'}>
          {error}
        </Text>
      )}
      <Button onClick={() => handleSubmit()} isLoading={isPending} mt={4} w={'full'} h={'52px'} rounded={'2xl'}>
        {t('common.claim')}
      </Button>
    </Flex>
  )
}

export const usePromoCodeForm = (initialCode?: string) => {
  const { account, signIn } = useSession()
  const [error, setError] = useState<string | null>(null)
  const [code, setCode] = useState<string>(initialCode ? initialCode : '')
  const api = useSecurityApi()

  const { t } = useTranslation()

  const { mutateAsync, isPending, data } = useMutation({
    mutationFn: async () => {
      if (!account) {
        signIn()
        return null
      }

      if (!code || code.length < 3) {
        setError(promoCodeErrorsMap('PROMO_CODE_NOT_FOUND', t))
        return null
      }

      const result = await api.apiRequest(`${API_GATEWAY}/promo/redeem`, { code })

      if (result?.error) {
        posthog.capture('promo_code_error', {
          error: result.error,
          code,
        })
        setError(promoCodeErrorsMap(result.error, t))
        return null
      }

      if (!result?.data) {
        posthog.capture('promo_code_error', {
          error: 'NO_DATA',
          code,
        })
        setError(promoCodeErrorsMap('', t))
        return null
      }

      posthog.capture('promo_code_claimed', { code })

      return {
        quantity: result.data.reward.split('|').at(0),
        denom: result.data.reward.split('|').at(1),
      } as Reward
    },
  })

  return {
    handleSubmit: mutateAsync,
    isPending,
    error,
    setCode: (code: string) => {
      setCode(code)
      setError(null)
    },
    reward: data ? data : null,
    code,
  }
}

const promoCodeErrorsMap = (errorCode: string, t: (key: string) => string) => {
  switch (errorCode) {
    case 'PROMO_CODE_ALREADY_USED':
      return t('common.promo-already-used')
    case 'PROMO_CODE_EXHAUSTED':
      return t('common.promo-usage-limit')
    case 'PROMO_CODE_EXPIRED':
      return t('common.promo-expired')
    case 'PROMO_CODE_NOT_FOUND':
      return t('common.promo-invalid')
    case 'USER_DOES_NOT_HAVE_GAME':
      return t('promo.must-play-games')

    case 'PROMO_USER_NOT_ELIGIBLE_INJ_COMMUNITY_BUY_BUCK':
      return t('promo.errors.not-eligible-inj-community-buy-buck')
    default:
      return t('common.promo-invalid')
  }
}
